import { useState, FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ProductShell, Button, Logo } from '@cloudgenie/ui';
import { API_BASE, useAuth } from '../AuthContext';

const GOOGLE_LOGIN_PATH = '/api/auth/google-native/login';

export default function LoginPage() {
  const [params] = useSearchParams();
  const next = params.get('next') || '';
  const { refresh } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Non-null while the server awaits a TOTP code (MFA-enrolled admin). */
  const [mfaChallenge, setMfaChallenge] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  /** True when the last login failure was likely because the user signed
   *  up via Google and never set a password. We can't tell for sure
   *  (backend deliberately does not enumerate), but we can nudge them
   *  toward the Google button on the SECOND failure. */
  const [failedOnce, setFailedOnce] = useState(false);

  function onLoggedIn() {
    if (next && /^https?:\/\//.test(next)) {
      window.location.href = next;
    } else {
      window.location.href = '/';
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/password-login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: email, password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setFailedOnce(true);
        setError(formatError(data?.detail) || 'Sign in failed. Please try again.');
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (data?.mfa_required && data?.challenge) {
        setMfaChallenge(data.challenge);
        setMfaCode('');
        return;
      }
      await refresh();
      onLoggedIn();
    } finally {
      setBusy(false);
    }
  }

  async function submitMfa(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/mfa/verify-login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge: mfaChallenge, code: mfaCode }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = formatError(data?.detail) || 'Verification failed.';
        setError(msg);
        if (res.status === 429 || /expired|invalid — sign in/i.test(msg)) {
          setMfaChallenge(null);
          setMfaCode('');
        }
        return;
      }
      await refresh();
      onLoggedIn();
    } finally {
      setBusy(false);
    }
  }

  function goGoogle() {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Preserve `next` across the OAuth roundtrip via ?next=. The backend
    // stashes it in the Starlette session and pops it in /callback after
    // allow-list validation. Do NOT use ?state=, which Authlib reserves.
    const q = next ? `?next=${encodeURIComponent(next)}` : '';
    window.location.href = `${API_BASE}${GOOGLE_LOGIN_PATH}${q}`;
  }

  return (
    <ProductShell product="id" title="" subtitle="">
      <div className="max-w-md mx-auto mt-4 mb-16" data-testid="login-card">
        <div className="rounded-2xl border border-border/60 bg-card/60 backdrop-blur-md shadow-2xl shadow-primary/5 p-8">
          <div className="flex flex-col items-center text-center mb-8">
            <Logo product="id" size={44} />
            <h1 className="mt-4 text-2xl font-semibold tracking-tight">{mfaChallenge ? 'Two-factor verification' : 'Welcome back'}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {mfaChallenge ? 'Enter the 6-digit code from your authenticator app.' : 'Sign in to your CloudGenie account.'}
            </p>
          </div>

          {mfaChallenge ? (
            <form onSubmit={submitMfa} className="space-y-4" data-testid="mfa-form">
              <div>
                <label className="block text-xs font-medium mb-1.5 text-muted-foreground" htmlFor="mfa-code">Authentication code</label>
                <input id="mfa-code" data-testid="mfa-code-input"
                  type="text" required autoFocus autoComplete="one-time-code"
                  inputMode="numeric" placeholder="123456" maxLength={16}
                  value={mfaCode} onChange={(e) => setMfaCode(e.target.value)}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-center text-lg tracking-[.35em] font-mono focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                />
                <p className="mt-2 text-[11px] text-muted-foreground">Lost your device? Enter one of your recovery codes instead.</p>
              </div>
              {error && (
                <div data-testid="mfa-error" className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-400 font-medium">{error}</div>
              )}
              <Button variant="primary" type="submit" disabled={busy || mfaCode.trim().length < 6}
                data-testid="mfa-submit" className="w-full">
                {busy ? 'Verifying…' : 'Verify & sign in'}
              </Button>
              <button type="button" data-testid="mfa-back"
                onClick={() => { setMfaChallenge(null); setMfaCode(''); setError(null); }}
                className="w-full text-xs text-muted-foreground hover:text-foreground transition">
                ← Back to sign in
              </button>
            </form>
          ) : (
          <>
          {/* Google is the primary path for most users — surface it FIRST. */}
          <button
            type="button"
            onClick={goGoogle}
            data-testid="login-google"
            className="w-full flex items-center justify-center gap-3 rounded-lg border border-border bg-background hover:bg-card transition px-4 py-2.5 text-sm font-medium"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.21 1.13-.84 2.09-1.79 2.73v2.27h2.9c1.7-1.57 2.69-3.87 2.69-6.64z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.27c-.8.54-1.83.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.96v2.34C2.44 15.98 5.48 18 9 18z" fill="#34A853"/>
              <path d="M3.95 10.71c-.18-.54-.28-1.12-.28-1.71 0-.59.1-1.17.28-1.71V4.96H.96C.35 6.17 0 7.55 0 9c0 1.45.35 2.83.96 4.04l2.99-2.33z" fill="#FBBC05"/>
              <path d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.88 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.99 2.34C4.66 5.17 6.65 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          <div className="relative py-5">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border/60" /></div>
            <span className="relative flex justify-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground bg-card/60 px-3">or use your password</span>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="block text-xs font-medium mb-1.5 text-muted-foreground" htmlFor="email">Email or username</label>
              <input id="email" data-testid="login-email"
                type="text" required autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-medium text-muted-foreground" htmlFor="password">Password</label>
                <Link to={`/forgot-password${next ? `?next=${encodeURIComponent(next)}` : ''}`}
                  className="text-xs text-primary hover:underline"
                  data-testid="link-forgot-password">
                  Forgot password?
                </Link>
              </div>
              <input id="password" data-testid="login-password"
                type="password" required autoComplete="current-password" minLength={8}
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
              />
            </div>
            {error && (
              <div data-testid="login-error" className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-400 space-y-1">
                <div className="font-medium">{error}</div>
                {failedOnce && (
                  <div className="text-muted-foreground">
                    Signed up with Google? <button type="button" onClick={goGoogle} className="text-primary hover:underline" data-testid="login-error-google">Continue with Google</button> above.
                    Or <Link to="/forgot-password" className="text-primary hover:underline">reset your password</Link>.
                  </div>
                )}
              </div>
            )}
            <Button variant="primary" type="submit" disabled={busy || !email || !password}
              data-testid="login-submit" className="w-full">
              {busy ? 'Signing in…' : 'Sign in with password'}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            New to CloudGenie?{' '}
            <Link to={`/signup${next ? `?next=${encodeURIComponent(next)}` : ''}`} className="text-primary hover:underline" data-testid="link-signup">
              Create an account
            </Link>
          </p>
          </>
          )}
        </div>

        <p className="mt-6 text-center text-[11px] text-muted-foreground">
          By signing in you agree to our{' '}
          <a href="https://www.cloudgenie.co/legal/terms" className="hover:text-foreground underline underline-offset-4">Terms</a>
          {' '}and{' '}
          <a href="https://www.cloudgenie.co/legal/privacy" className="hover:text-foreground underline underline-offset-4">Privacy Policy</a>.
        </p>
      </div>
    </ProductShell>
  );
}

function formatError(detail: unknown): string {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e: any) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  }
  if (typeof detail === 'object') return JSON.stringify(detail);
  return String(detail);
}
